import type {
  AiReportContentLanguage,
  AiReportDetail,
  AiReportListItem,
  AiReportQuarter,
} from "@/api/ApiAiReport";

const SAMPLE_CONTENT_JA = `こんにちは、保育ロボットのVEVOです。
太郎君の成長についてお伝えします。`;

const SAMPLE_CONTENT_EN = `Hello, I am VEVO, the childcare robot.
I would like to share Taro's growth with you.`;

const SAMPLE_CONTENT_KM = `សួស្តី ខ្ញុំគឺ VEVO រ៉ូបូតថែទាំកុមារ។
ខ្ញុំចង់ចែករំលែកអំពីការលូតលាស់របស់ Taro។`;

const SAMPLE_CONTENT_VI = `Xin chào, tôi là VEVO, robot chăm sóc trẻ em.
Tôi muốn chia sẻ về sự phát triển của Taro với quý phụ huynh.`;

interface MockReportEntry {
  childId: number;
  childLastName: string;
  childFirstName: string;
  ageYears: number;
  ageMonths: number;
  birthYearMonth: string;
  reports: Array<{
    businessYear: number;
    quarter: AiReportQuarter;
    monthsLabel: string;
    titleSnapshotYearMonth: string;
    titleAgeMonthsAtSnapshot: number;
    publishedAt: string;
    dataCoverageMonths: number[];
    contentEn: string;
    contentJa: string;
    contentKm: string;
    /** 翻訳済みなら翻訳先言語コード。未翻訳なら null */
    contentLanguage?: AiReportContentLanguage | null;
    extraContent?: string | null;
  }>;
}

const MOCK_REPORTS: Record<number, MockReportEntry> = {
  101: {
    childId: 101,
    childLastName: "貞松",
    childFirstName: "太郎",
    ageYears: 2,
    ageMonths: 11,
    birthYearMonth: "2023-12",
    reports: [
      {
        businessYear: 2025,
        quarter: 4,
        monthsLabel: "5-7",
        titleSnapshotYearMonth: "7/2026",
        titleAgeMonthsAtSnapshot: 32,
        publishedAt: new Date(Date.now() - 86400_000 * 2).toISOString(),
        dataCoverageMonths: [5, 6, 7],
        contentEn: SAMPLE_CONTENT_EN,
        contentJa: SAMPLE_CONTENT_JA,
        contentKm: SAMPLE_CONTENT_KM,
        // Case2 デモ: 園がベトナム語へ翻訳
        contentLanguage: "vi",
        extraContent: SAMPLE_CONTENT_VI,
      },
      {
        businessYear: 2025,
        quarter: 3,
        monthsLabel: "2-4",
        titleSnapshotYearMonth: "4/2026",
        titleAgeMonthsAtSnapshot: 29,
        publishedAt: new Date(Date.now() - 86400_000 * 60).toISOString(),
        dataCoverageMonths: [2, 3, 4],
        contentEn: SAMPLE_CONTENT_EN,
        contentJa: SAMPLE_CONTENT_JA,
        contentKm: SAMPLE_CONTENT_KM,
        contentLanguage: null,
      },
      {
        businessYear: 2025,
        quarter: 2,
        monthsLabel: "11-1",
        titleSnapshotYearMonth: "1/2026",
        titleAgeMonthsAtSnapshot: 26,
        publishedAt: new Date(Date.now() - 86400_000 * 120).toISOString(),
        dataCoverageMonths: [11, 1],
        contentEn: SAMPLE_CONTENT_EN,
        contentJa: SAMPLE_CONTENT_JA,
        contentKm: SAMPLE_CONTENT_KM,
        contentLanguage: null,
      },
    ],
  },
};

function formatAge(ageMonths: number): string {
  const years = Math.floor(ageMonths / 12);
  const months = ageMonths % 12;
  const yearPart = `${years} ${years === 1 ? "year" : "years"}`;
  if (months === 0) return yearPart;
  return `${yearPart} ${months} ${months === 1 ? "month" : "months"}`;
}

function buildItem(
  entry: MockReportEntry,
  r: MockReportEntry["reports"][number],
  detail: boolean,
): AiReportDetail | AiReportListItem {
  const title = `VEVO Report - ${formatAge(r.titleAgeMonthsAtSnapshot)} (${r.titleSnapshotYearMonth})`;
  const base: AiReportListItem = {
    childId: entry.childId,
    childLastName: entry.childLastName,
    childFirstName: entry.childFirstName,
    businessYear: r.businessYear,
    quarter: r.quarter,
    monthsLabel: r.monthsLabel,
    title,
    publishedAt: r.publishedAt,
    dataCoverageMonths: r.dataCoverageMonths,
  };
  if (detail) {
    const contentLanguage = r.contentLanguage ?? null;
    let content = r.contentJa;
    if (contentLanguage === "en") content = r.contentEn;
    else if (contentLanguage === "ja") content = r.contentJa;
    else if (contentLanguage === "km") content = r.contentKm;
    else if (contentLanguage != null) content = r.extraContent ?? r.contentEn;
    return {
      ...base,
      content,
      contentEn: r.contentEn,
      contentJa: r.contentJa,
      contentKm: r.contentKm,
      contentLanguage,
    };
  }
  return base;
}

function delay<T>(value: T, ms = 250): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export function mockListPublished(childId: number): Promise<AiReportListItem[]> {
  const entry = MOCK_REPORTS[childId] ?? Object.values(MOCK_REPORTS)[0];
  if (!entry) return delay([]);
  const items = entry.reports.map((r) => buildItem(entry, r, false) as AiReportListItem);
  return delay(items.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1)));
}

export function mockGetPublished(
  childId: number,
  businessYear: number,
  quarter: AiReportQuarter,
): Promise<AiReportDetail | null> {
  const entry = MOCK_REPORTS[childId] ?? Object.values(MOCK_REPORTS)[0];
  if (!entry) return delay(null);
  const report = entry.reports.find(
    (r) => r.businessYear === businessYear && r.quarter === quarter,
  );
  if (!report) return delay(null);
  return delay(buildItem(entry, report, true) as AiReportDetail);
}
